import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const DriverResultCard = ({ driver, onCallDispatch, onDriverUpdated }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [yards, setYards] = useState([]);
  const [form, setForm] = useState({
    phone: '',
    yardId: '',
    unitOverride: '',
    vinOverride: '',
  });

  // Load yards for dropdown
  useEffect(() => {
    const fetchYards = async () => {
      const { data } = await supabase?.from('yards')?.select('id, name')?.eq('is_active', true)?.order('name');
      if (data) setYards(data);
    };
    fetchYards();
  }, []);

  // Pre-populate form when driver data changes or edit opens
  useEffect(() => {
    if (driver) {
      setForm({
        phone: driver?.phone || '',
        yardId: driver?.yardId || '',
        unitOverride: driver?.unitOverride || '',
        vinOverride: driver?.vinOverride || '',
      });
    }
  }, [driver]);

  const getStatusColor = (status) => {
    return status === 'active' ?'bg-success/10 text-success border-success/20' :'bg-muted text-muted-foreground border-border';
  };

  const getStatusIcon = (status) => {
    return status === 'active' ? 'CheckCircle2' : 'XCircle';
  };

  const handleRequestService = () => {
    localStorage.setItem('currentDriverPhone', driver?.phone);
    navigate('/service-request-form', {
      state: {
        driverPhone: driver?.phone,
        unitNumber: driver?.unitNumber,
        vin: driver?.vin,
        yardName: driver?.yardName,
      },
    });
  };

  const handleEditToggle = () => {
    setSaveError(null);
    setSaveSuccess(false);
    setIsEditing((prev) => !prev);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Normalize the new phone to E.164 for both the RPC call and the re-fetch
      const rawDigits = form?.phone?.replace(/\D/g, '') || '';
      const newE164 =
        rawDigits?.length === 10
          ? `+1${rawDigits}`
          : rawDigits?.length === 11 && rawDigits?.startsWith('1')
          ? `+${rawDigits}`
          : driver?.e164Phone; // fallback to original if unparseable

      const { error } = await supabase?.rpc('update_driver_self', {
        p_current_phone: driver?.e164Phone || driver?.phone,
        p_new_phone: newE164,
        p_yard_id: form?.yardId || null,
        p_unit_override: form?.unitOverride || null,
        p_vin_override: form?.vinOverride || null,
      });

      if (error) {
        setSaveError(error?.message || 'Failed to save changes. Please try again.');
        return;
      }

      setSaveSuccess(true);
      setIsEditing(false);

      // Pass the E.164 phone so the parent re-fetches with the correct lookup key
      if (onDriverUpdated) {
        onDriverUpdated(newE164);
      }
    } catch (err) {
      setSaveError(err?.message || 'An unexpected error occurred.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2">
      {/* Header */}
      <div className="bg-primary/5 border-b border-border px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full flex-shrink-0">
              <Icon name="User" size={24} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{driver?.name}</h3>
              <p className="text-sm text-muted-foreground">{driver?.phone}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-full border flex-shrink-0 ${getStatusColor(driver?.status)}`}>
              <Icon name={getStatusIcon(driver?.status)} size={16} strokeWidth={2} />
              <span className="text-xs font-medium capitalize">{driver?.status}</span>
            </div>
            <button
              onClick={handleEditToggle}
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full border border-border bg-background hover:bg-muted/50 transition-colors text-xs font-medium text-foreground flex-shrink-0"
            >
              <Icon name={isEditing ? 'X' : 'Pencil'} size={14} strokeWidth={2} />
              <span>{isEditing ? 'Cancel' : 'Edit My Info'}</span>
            </button>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-4">
        {/* Success banner */}
        {saveSuccess && (
          <div className="flex items-center space-x-2 p-3 bg-success/10 border border-success/20 rounded-lg text-sm text-success">
            <Icon name="CheckCircle2" size={16} strokeWidth={2} />
            <span>Your information has been updated successfully.</span>
          </div>
        )}

        {/* Error banner */}
        {saveError && (
          <div className="flex items-center space-x-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            <Icon name="AlertCircle" size={16} strokeWidth={2} />
            <span>{saveError}</span>
          </div>
        )}

        {isEditing ? (
          /* ── EDIT MODE ── */
          (<div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Update your current information below. Unit Number and VIN are free-text — enter whatever is on your truck/trailer today.
            </p>
            {/* Phone */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Phone Number</label>
              <input
                type="tel"
                value={form?.phone}
                onChange={(e) => handleFormChange('phone', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                placeholder="e.g. +15551234567"
              />
            </div>
            {/* Yard dropdown */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Yard Location</label>
              <select
                value={form?.yardId}
                onChange={(e) => handleFormChange('yardId', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                <option value="">— Select a yard —</option>
                {yards?.map((y) => (
                  <option key={y?.id} value={y?.id}>
                    {y?.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Unit Number override */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Unit Number <span className="text-muted-foreground font-normal">(your current truck/trailer)</span>
              </label>
              <input
                type="text"
                value={form?.unitOverride}
                onChange={(e) => handleFormChange('unitOverride', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                placeholder="e.g. T-4821"
              />
            </div>
            {/* VIN override */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                VIN <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={form?.vinOverride}
                onChange={(e) => handleFormChange('vinOverride', e?.target?.value)}
                className="w-full px-3 py-2 text-sm font-mono border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                placeholder="17-character VIN"
                maxLength={17}
              />
            </div>
            {/* Save / Cancel */}
            <div className="flex gap-2 pt-1">
              <Button
                variant="default"
                size="sm"
                iconName={isSaving ? 'Loader2' : 'Save'}
                iconPosition="left"
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1"
              >
                {isSaving ? 'Saving…' : 'Save Changes'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditToggle}
                disabled={isSaving}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>)
        ) : (
          /* ── VIEW MODE ── */
          (<>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg flex-shrink-0">
                  <Icon name="Truck" size={20} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-1">Unit Number</p>
                  {driver?.unitNumber
                    ? <p className="text-base font-semibold text-foreground truncate">{driver?.unitNumber}</p>
                    : <p className="text-sm text-muted-foreground italic">No unit currently assigned</p>
                  }
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg flex-shrink-0">
                  <Icon name="Hash" size={20} color="var(--color-accent)" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-1">VIN</p>
                  {driver?.vin
                    ? <p className="text-sm font-mono text-foreground truncate">{driver?.vin}</p>
                    : <p className="text-sm text-muted-foreground italic">—</p>
                  }
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-muted/30 rounded-lg md:col-span-2">
                <div className="flex items-center justify-center w-10 h-10 bg-success/10 rounded-lg flex-shrink-0">
                  <Icon name="MapPin" size={20} color="var(--color-success)" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-1">Current Yard Location</p>
                  {driver?.yardName
                    ? <>
                        <p className="text-base font-semibold text-foreground">{driver?.yardName}</p>
                        {driver?.yardAddress && <p className="text-sm text-muted-foreground mt-1">{driver?.yardAddress}</p>}
                      </>
                    : <p className="text-sm text-muted-foreground italic">No yard assigned</p>
                  }
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-border space-y-3">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Calendar" size={16} strokeWidth={2} />
                <span>Assignment: {driver?.assignmentDate}</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="History"
                  iconPosition="left"
                  onClick={() => {
                    localStorage.setItem('currentDriverPhone', driver?.phone);
                    navigate('/service-request-history');
                  }}
                  className="w-full sm:w-auto"
                >
                  Request History
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Phone"
                  iconPosition="left"
                  onClick={onCallDispatch}
                  className="w-full sm:w-auto"
                >
                  Call Dispatch
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Wrench"
                  iconPosition="left"
                  onClick={handleRequestService}
                  className="w-full sm:w-auto"
                >
                  Request Service
                </Button>
              </div>
            </div>
          </>)
        )}
      </div>
    </div>
  );
};

export default DriverResultCard;